<script setup lang="ts">
import { ref } from 'vue'
import FeedbackButton from './FeedbackButton.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import type { AnalyticsData } from '@/tracking/analytics/analyticsTracker'
import { FEEDBACK_SUBMISSION_DELAY_MS, type FeedbackValue, type ThumbFeedbackValue } from './feedback_buttons'
import { SUBMITTED_FEEDBACK_EVENT_NAMES } from './feedback_event_names'

const props = withDefaults(defineProps<{
  eventData?: AnalyticsData
}>(), {
  eventData: () => ({}),
})

const analytics = getAnalyticsTracker()

const activeThumb = ref<ThumbFeedbackValue | null>(null)
const isFlagged = ref(false)
const submittedFeedback = new Set<FeedbackValue>()

function submitFeedbackOnce(feedback: FeedbackValue) {
  if (submittedFeedback.has(feedback)) {
    return
  }
  analytics.sendEvent(SUBMITTED_FEEDBACK_EVENT_NAMES[feedback], props.eventData)
  submittedFeedback.add(feedback)
}

// Each click schedules a check of whatever is selected when its delay ends, as Vue 2 does. So a
// selection made shortly before an earlier click's check can be sent with less than the full delay.
function scheduleFeedbackSubmission() {
  setTimeout(() => {
    if (activeThumb.value) {
      submitFeedbackOnce(activeThumb.value)
    }
    if (isFlagged.value) {
      submitFeedbackOnce('flag')
    }
  }, FEEDBACK_SUBMISSION_DELAY_MS)
}

function toggleThumb(thumb: ThumbFeedbackValue) {
  activeThumb.value = activeThumb.value === thumb ? null : thumb
  scheduleFeedbackSubmission()
}

function toggleFlag() {
  isFlagged.value = !isFlagged.value
  scheduleFeedbackSubmission()
}
</script>

<template>
  <div>
    <FeedbackButton
      icon-name="thumb_up"
      tooltip-hover-text="I like this content."
      :is-active="activeThumb === 'like'"
      @updated="toggleThumb('like')"
    />
    <FeedbackButton
      icon-name="thumb_down"
      tooltip-hover-text="I don't like this content."
      :is-active="activeThumb === 'dislike'"
      @updated="toggleThumb('dislike')"
    />
    <FeedbackButton
      icon-name="flag"
      tooltip-hover-text="This content is harmful."
      :is-active="isFlagged"
      @updated="toggleFlag"
    />
  </div>
</template>
